import { useEffect, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Calculator } from 'lucide-react';
import type {
  SimplesAnnexId,
  TaxReformSectorReduction,
  TaxReformSimulationResult,
} from '@crm/shared';
import { Badge, Button, Card, EmptyState } from '../../../ui';
import { LoadingSpinner } from '../../../components/common/LoadingSpinner';
import { ChartPanel } from '../../../components/atlas/ChartPanel';
import { useChartColors } from '../../../utils/chartTokens';
import {
  useCompanies,
  useSimulateTaxReform,
  useTaxReformDefaults,
} from '../../../hooks/useFinancialOps';

const ANNEX_OPTIONS: { value: SimplesAnnexId; label: string }[] = [
  { value: 'I', label: 'Anexo I — Comércio' },
  { value: 'II', label: 'Anexo II — Indústria' },
  { value: 'III', label: 'Anexo III — Serviços (Fator R ≥ 28%)' },
  { value: 'IV', label: 'Anexo IV — Construção, advocacia, limpeza' },
  { value: 'V', label: 'Anexo V — Serviços (Fator R < 28%)' },
];

const REDUCTION_OPTIONS: { value: TaxReformSectorReduction; label: string }[] = [
  { value: 'STANDARD', label: 'Padrão (sem redução)' },
  { value: 'REDUCED_30', label: '−30% (profissões regulamentadas)' },
  { value: 'REDUCED_60', label: '−60% (saúde, educação, agro etc.)' },
];

function formatCurrency(amount: number) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(amount);
}

function formatPercent(fraction: number) {
  return `${(fraction * 100).toFixed(2).replace('.', ',')}%`;
}

const inputClass = 'h-9 border border-border rounded-md px-3 text-sm bg-surface text-ink w-full';
const labelClass = 'block text-xs font-medium text-ink-secondary uppercase tracking-wider mb-1.5';

export function SimuladorReformaPage() {
  const { data: companiesData } = useCompanies({ active: true });
  const companies = companiesData?.data ?? [];

  const [companyId, setCompanyId] = useState('');
  const [rbt12, setRbt12] = useState('');
  const [annualRevenue, setAnnualRevenue] = useState('');
  const [annex, setAnnex] = useState<SimplesAnnexId>('III');
  const [purchases, setPurchases] = useState('');
  const [b2bShare, setB2bShare] = useState('0');
  const [reduction, setReduction] = useState<TaxReformSectorReduction>('STANDARD');
  const [referenceRate, setReferenceRate] = useState('26.5');

  const { data: defaultsData } = useTaxReformDefaults(companyId);
  const simulate = useSimulateTaxReform();
  const result: TaxReformSimulationResult | undefined = simulate.data?.data;

  useEffect(() => {
    if (!companyId && companies.length === 1) {
      setCompanyId(companies[0].id);
    }
  }, [companies, companyId]);

  // Pré-preenche o RBT12 com a receita dos últimos 12 meses da empresa.
  useEffect(() => {
    const suggested = defaultsData?.data?.suggestedRbt12;
    if (suggested && !rbt12) {
      setRbt12(String(Math.round(suggested)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultsData]);

  const company = companies.find((c: any) => c.id === companyId);
  const notSimples =
    company && company.taxRegime !== 'SIMPLES_NACIONAL' && company.taxRegime !== 'MEI';

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!rbt12) {
      return;
    }
    simulate.mutate({
      companyId: companyId || undefined,
      rbt12: Number(rbt12),
      annualRevenue: annualRevenue ? Number(annualRevenue) : undefined,
      annex,
      creditablePurchases: purchases ? Number(purchases) : 0,
      b2bSharePercent: Number(b2bShare) || 0,
      sectorReduction: reduction,
      referenceRatePercent: Number(referenceRate) || 26.5,
    });
  }

  return (
    <div className="space-y-6">
      <Card padding="md">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <label className={labelClass}>Empresa</label>
              <select
                className={inputClass}
                value={companyId}
                onChange={(e) => {
                  setCompanyId(e.target.value);
                  setRbt12('');
                }}
              >
                <option value="">Simulação avulsa...</option>
                {companies.map((c: any) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass}>RBT12 — receita 12 meses (R$)</label>
              <input
                type="number"
                min="1"
                max="4800000"
                step="0.01"
                required
                className={inputClass}
                value={rbt12}
                onChange={(e) => setRbt12(e.target.value)}
                placeholder="Ex.: 600000"
              />
            </div>
            <div>
              <label className={labelClass}>Receita anual projetada (R$)</label>
              <input
                type="number"
                min="1"
                step="0.01"
                className={inputClass}
                value={annualRevenue}
                onChange={(e) => setAnnualRevenue(e.target.value)}
                placeholder="Igual ao RBT12"
              />
            </div>
            <div>
              <label className={labelClass}>Anexo do Simples</label>
              <select
                className={inputClass}
                value={annex}
                onChange={(e) => setAnnex(e.target.value as SimplesAnnexId)}
              >
                {ANNEX_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass}>Compras creditáveis/ano (R$)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                className={inputClass}
                value={purchases}
                onChange={(e) => setPurchases(e.target.value)}
                placeholder="Insumos, serviços, aluguel..."
              />
            </div>
            <div>
              <label className={labelClass}>Vendas B2B (% da receita)</label>
              <input
                type="number"
                min="0"
                max="100"
                step="1"
                className={inputClass}
                value={b2bShare}
                onChange={(e) => setB2bShare(e.target.value)}
              />
            </div>
            <div>
              <label className={labelClass}>Redução setorial IBS/CBS</label>
              <select
                className={inputClass}
                value={reduction}
                onChange={(e) => setReduction(e.target.value as TaxReformSectorReduction)}
              >
                {REDUCTION_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass}>Alíquota de referência (%)</label>
              <input
                type="number"
                min="15"
                max="40"
                step="0.1"
                className={inputClass}
                value={referenceRate}
                onChange={(e) => setReferenceRate(e.target.value)}
              />
            </div>
          </div>

          {notSimples && (
            <p className="text-sm text-warning">
              Esta empresa está cadastrada como{' '}
              {company.taxRegime === 'LUCRO_PRESUMIDO' ? 'Lucro Presumido' : 'Lucro Real'} — o
              simulador cobre apenas o Simples Nacional.
            </p>
          )}

          <div className="flex items-center gap-3">
            <Button type="submit" loading={simulate.isPending} icon={<Calculator size={16} />}>
              Simular
            </Button>
            {simulate.isError && (
              <p className="text-sm text-danger">
                {(simulate.error as any)?.response?.data?.error ??
                  (simulate.error as any)?.response?.data?.message ??
                  'Não foi possível simular. Verifique os valores.'}
              </p>
            )}
          </div>
        </form>
      </Card>

      {simulate.isPending ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      ) : !result ? (
        <EmptyState
          title="Simule a Reforma Tributária"
          body="Informe o RBT12 e o anexo da sua empresa para comparar, ano a ano (2026–2033), permanecer 100% no Simples versus o regime híbrido com IBS/CBS por fora (LC 214/2025)."
        />
      ) : (
        <SimulationResult result={result} />
      )}
    </div>
  );
}

function SimulationResult({ result }: { result: TaxReformSimulationResult }) {
  const colors = useChartColors();
  const final = result.years[result.years.length - 1];
  const hybridWins = result.summary.cheaperScenario2033 === 'HYBRID';

  const chartData = result.years.map((y) => ({
    year: String(y.year),
    simples: y.fullSimples.total,
    hibrido: y.hybrid.total,
  }));

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <SummaryCard
          label="Alíquota efetiva atual"
          value={formatPercent(result.effectiveSimplesRate)}
          detail={`DAS anual: ${formatCurrency(result.currentAnnualDas)}`}
        />
        <SummaryCard
          label="Carga em 2033 — Simples cheio"
          value={formatCurrency(final.fullSimples.total)}
          detail={`Crédito p/ clientes B2B: ${formatCurrency(final.fullSimples.clientCredit)}/ano`}
        />
        <SummaryCard
          label="Carga em 2033 — Híbrido"
          value={formatCurrency(final.hybrid.total)}
          detail={`Crédito p/ clientes B2B: ${formatCurrency(final.hybrid.clientCredit)}/ano`}
        />
        <SummaryCard
          label={hybridWins ? 'Economia anual do híbrido (2033)' : 'Custo extra do híbrido (2033)'}
          value={formatCurrency(Math.abs(result.summary.hybridSavings2033))}
          detail={`Vantagem de crédito B2B: ${formatCurrency(result.summary.competitiveEdge2033)}/ano`}
          badge={
            <Badge tone={hybridWins ? 'accent' : 'info'}>
              {hybridWins ? 'Híbrido vence' : 'Simples cheio vence'}
            </Badge>
          }
        />
      </div>

      <Card padding="md">
        <h3 className="font-serif text-base text-ink-primary mb-1">Recomendação</h3>
        <p className="text-sm text-ink-secondary">{result.summary.recommendation}</p>
      </Card>

      <ChartPanel
        title="Carga tributária anual na transição (2026–2033)"
        subtitle="Simples cheio (DAS) vs. híbrido (DAS reduzido + IBS/CBS por fora, líquido de créditos)"
      >
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} barGap={2} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={colors.border} vertical={false} />
            <XAxis
              dataKey="year"
              tick={{ fill: colors.inkTer, fontSize: 12 }}
              stroke={colors.border}
            />
            <YAxis
              tick={{ fill: colors.inkTer, fontSize: 12 }}
              stroke={colors.border}
              tickFormatter={(v: number) =>
                new Intl.NumberFormat('pt-BR', { notation: 'compact' }).format(v)
              }
            />
            <Tooltip
              formatter={(value, name) => [
                formatCurrency(Number(value ?? 0)),
                name === 'simples' ? 'Simples cheio' : 'Híbrido',
              ]}
              labelFormatter={(label) => {
                const year = result.years.find((y) => String(y.year) === String(label));
                return year ? `${label} — ${year.phase}` : label;
              }}
              contentStyle={{
                backgroundColor: colors.surface,
                border: `1px solid ${colors.border}`,
                borderRadius: 8,
                fontSize: 12,
              }}
            />
            <Legend
              formatter={(value: string) => (
                <span style={{ color: colors.inkSec, fontSize: 12 }}>
                  {value === 'simples' ? 'Simples cheio' : 'Híbrido'}
                </span>
              )}
            />
            <Bar dataKey="simples" fill={colors.info} radius={[3, 3, 0, 0]} maxBarSize={28} />
            <Bar dataKey="hibrido" fill={colors.accent} radius={[3, 3, 0, 0]} maxBarSize={28} />
          </BarChart>
        </ResponsiveContainer>
      </ChartPanel>

      <Card padding="md">
        <h3 className="font-serif text-base text-ink-primary mb-3">Detalhamento ano a ano</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-subtle text-ink-secondary">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Ano</th>
                <th className="text-left px-3 py-2 font-medium">Fase da transição</th>
                <th className="text-right px-3 py-2 font-medium">Simples cheio</th>
                <th className="text-right px-3 py-2 font-medium">Híbrido: DAS</th>
                <th className="text-right px-3 py-2 font-medium">Híbrido: IBS/CBS</th>
                <th className="text-right px-3 py-2 font-medium">Híbrido: total</th>
                <th className="text-right px-3 py-2 font-medium">Economia</th>
              </tr>
            </thead>
            <tbody>
              {result.years.map((y) => (
                <tr key={y.year} className="border-b border-border last:border-0">
                  <td className="px-3 py-2 text-ink-primary font-medium">{y.year}</td>
                  <td className="px-3 py-2 text-ink-secondary">{y.phase}</td>
                  <td className="px-3 py-2 text-right text-ink-primary">
                    {formatCurrency(y.fullSimples.total)}
                  </td>
                  <td className="px-3 py-2 text-right text-ink-secondary">
                    {y.hybridAvailable ? formatCurrency(y.hybrid.das) : '—'}
                  </td>
                  <td className="px-3 py-2 text-right text-ink-secondary">
                    {y.hybridAvailable ? formatCurrency(y.hybrid.ivaDue) : '—'}
                  </td>
                  <td className="px-3 py-2 text-right text-ink-primary">
                    {y.hybridAvailable ? formatCurrency(y.hybrid.total) : '—'}
                  </td>
                  <td
                    className={`px-3 py-2 text-right font-medium ${
                      y.savings > 0
                        ? 'text-accent'
                        : y.savings < 0
                          ? 'text-danger'
                          : 'text-ink-tertiary'
                    }`}
                  >
                    {y.hybridAvailable ? formatCurrency(y.savings) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card padding="md">
        <h3 className="font-serif text-base text-ink-primary mb-2">Premissas da simulação</h3>
        <ul className="list-disc pl-5 space-y-1 text-sm text-ink-secondary">
          {result.assumptions.map((a) => (
            <li key={a}>{a}</li>
          ))}
        </ul>
      </Card>
    </div>
  );
}

function SummaryCard({
  label,
  value,
  detail,
  badge,
}: {
  label: string;
  value: string;
  detail: string;
  badge?: React.ReactNode;
}) {
  return (
    <div className="border border-border rounded-lg p-4 bg-surface">
      <div className="flex items-start justify-between gap-2">
        <p className="text-xs text-ink-secondary uppercase tracking-wider font-medium">{label}</p>
        {badge}
      </div>
      <p className="text-xl font-semibold text-ink-primary mt-1">{value}</p>
      <p className="text-xs text-ink-tertiary mt-1">{detail}</p>
    </div>
  );
}
